import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';

export const useConfiguration = () => {
  const [rules, setRules] = useState(null);
  const [configuration, setConfiguration] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const debounceTimer = useRef(null);

  // Serialize configuration for stable dependency comparison
  const configString = useMemo(() => JSON.stringify(configuration), [configuration]);

  // Load product rules on mount and whenever configuration changes (debounced)
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      loadRules();
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [configString]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRules = async () => {
    try {
      setLoading(true);

      // Pass current configuration as query param for filtering
      const configParam = Object.keys(configuration).length > 0
        ? `?configuration=${encodeURIComponent(JSON.stringify(configuration))}`
        : '';

      const response = await axios.get(`/api/configuration/rules/adjustable-height-work-table${configParam}`);
      setRules(response.data.rules);
      setError(null);
    } catch (err) {
      setError('Failed to load configuration rules');
      console.error('Error loading rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateConfiguration = useCallback((stepKey, value) => {
    setConfiguration(prev => ({
      ...prev,
      [stepKey]: value
    }));
  }, []);

  const updateMultiSelectConfiguration = useCallback((stepKey, value, checked) => {
    setConfiguration(prev => {
      const currentValues = prev[stepKey] || [];
      if (checked) {
        return {
          ...prev,
          [stepKey]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [stepKey]: currentValues.filter(v => v !== value)
        };
      }
    });
  }, []);

  const nextStep = () => {
    if (rules && currentStep < rules.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && rules && stepIndex < rules.steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  const resetConfiguration = () => {
    setConfiguration({});
    setCurrentStep(0);
  };

  const isStepComplete = (step) => {
    if (!step.required) return true;

    if (step.multiSelect) {
      const values = configuration[step.key] || [];
      return values.length > 0;
    }

    return !!configuration[step.key];
  };

  const canProceed = () => {
    if (!rules || !rules.steps[currentStep]) return false;
    return isStepComplete(rules.steps[currentStep]);
  };

  return {
    rules,
    configuration,
    currentStep,
    loading,
    error,
    updateConfiguration,
    updateMultiSelectConfiguration,
    nextStep,
    previousStep,
    goToStep,
    resetConfiguration,
    isStepComplete,
    canProceed
  };
};
